import { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Select, InputNumber, message, Input, Divider, Typography, Popconfirm, Tooltip, Alert, Tabs, Statistic, Row, Col } from 'antd';
import { PlusOutlined, ReloadOutlined, DeleteOutlined, EditOutlined, ApiOutlined, ThunderboltOutlined, WalletOutlined, LoadingOutlined, BarChartOutlined, CalculatorOutlined, FileTextOutlined } from '@ant-design/icons';
import { providerAPI } from '../services/api';
import { accountNumber, todaySeedanceSummary } from '../utils/seedanceAccount';

const { Text, Title, Paragraph } = Typography;

const VIDEO_MODES = [
  { value: 't2v', label: '文生视频 (T2V)' },
  { value: 'i2v', label: '图生视频 (I2V)' },
  { value: 'firstlast', label: '首尾帧 (Start/End Frames)' },
  { value: 'smart_multiframe', label: '智能多帧 (Smart MultiFrame)' },
  { value: 'full_reference', label: '全能参考 (Full Reference)' },
];

// 定价全部走 video_config JSON 的 resolution_pricing / resolution_pricing_with_video

const SeedanceConfig = () => {
  const [providers, setProviders] = useState([]);
  const [modelConfigs, setModelConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('providers');
  const [providerModal, setProviderModal] = useState(false);
  const [modelModal, setModelModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [providerForm] = Form.useForm();
  const [modelForm] = Form.useForm();

  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [statsError, setStatsError] = useState('');
  const [billsError, setBillsError] = useState('');
  const selectedProviderRef = useRef(selectedProviderId);
  selectedProviderRef.current = selectedProviderId;
  const statsRequest = useRef(0), billsRequest = useRef(0);
  // 上游数据
  const [upstreamBalance, setUpstreamBalance] = useState(null);
  const [upstreamUsage, setUpstreamUsage] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [bills, setBills] = useState({ list: [], total: 0, total_consumption: 0 });
  const [billsLoading, setBillsLoading] = useState(false);
  const [billsPage, setBillsPage] = useState(1);

  const seedanceProviders = providers.filter(p => {
    const n = (p.name || '').toLowerCase();
    const d = (p.display_name || '').toLowerCase();
    return (n.includes('seedance') || d.includes('seedance'))
      && !n.includes('huoshan') && !n.includes('tokease') && !n.includes('词易')
      && !d.includes('huoshan') && !d.includes('tokease') && !d.includes('词易');
  });
  const accountProviders = seedanceProviders.filter(p => p.status === 'active');
  useEffect(() => {
    setSelectedProviderId(previous => accountProviders.some(p => p.id === previous) ? previous : (accountProviders[0]?.id || null));
  }, [providers]);
  const seedanceProviderIDs = seedanceProviders.map(p => p.id);
  const seedanceModels = modelConfigs.filter(m =>
    seedanceProviderIDs.includes(m.provider_id)
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([
        providerAPI.getProviders(),
        providerAPI.getModelConfigs(),
      ]);
      let providersData = [];
      if (Array.isArray(pRes)) providersData = pRes;
      else if (pRes?.data?.list) providersData = pRes.data.list;
      else if (pRes?.data) providersData = Array.isArray(pRes.data) ? pRes.data : [];
      setProviders(providersData);

      let modelsData = [];
      if (Array.isArray(mRes)) modelsData = mRes;
      else if (mRes?.data?.list) modelsData = mRes.data.list;
      else if (mRes?.data) modelsData = Array.isArray(mRes.data) ? mRes.data : [];
      setModelConfigs(modelsData);
    } catch (e) {
      message.error('加载数据失败: ' + (e.response?.data?.message || e.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const errorText = error => error?.response?.data?.message || error?.message || '查询失败，请稍后重试';
  const loadStats = async (providerId = selectedProviderId) => {
    if (!providerId) return;
    const requestId = ++statsRequest.current;
    setBalanceLoading(true); setStatsError('');
    const results = await Promise.allSettled([
      providerAPI.getSeedanceBalance({ provider_id: providerId }),
      providerAPI.getSeedanceUsage({ provider_id: providerId }),
    ]);
    if (selectedProviderRef.current !== providerId || statsRequest.current !== requestId) return;
    const errors = [];
    results.forEach((result, index) => {
      const label = index === 0 ? '余额' : '用量';
      if (result.status === 'fulfilled' && result.value?.code === 0 && result.value.data) {
        (index === 0 ? setUpstreamBalance : setUpstreamUsage)(result.value.data);
      } else {
        (index === 0 ? setUpstreamBalance : setUpstreamUsage)(null);
        errors.push(`${label}：${errorText(result.status === 'rejected' ? result.reason : result.value)}`);
      }
    });
    setStatsError(errors.join('；')); setBalanceLoading(false);
  };
  const loadBills = async (page = 1, providerId = selectedProviderId) => {
    if (!providerId) return;
    const requestId = ++billsRequest.current;
    setBillsLoading(true); setBillsError('');
    try {
      const res = await providerAPI.getSeedanceBills({ provider_id: providerId, page, page_size: 15 });
      if (selectedProviderRef.current !== providerId || billsRequest.current !== requestId) return;
      if (res?.code !== 0 || !res.data) throw new Error(res?.message || '账单返回格式异常');
      setBills(res.data); setBillsPage(page);
    } catch (error) {
      if (selectedProviderRef.current === providerId && billsRequest.current === requestId) {
        setBills({ list: [], total: 0, total_consumption: 0 }); setBillsError(errorText(error));
      }
    } finally {
      if (selectedProviderRef.current === providerId && billsRequest.current === requestId) setBillsLoading(false);
    }
  };
  useEffect(() => {
    setUpstreamBalance(null); setUpstreamUsage(null);
    setBills({ list: [], total: 0, total_consumption: 0 });
    setStatsError(''); setBillsError('');
    if (selectedProviderId) { loadStats(selectedProviderId); loadBills(1, selectedProviderId); }
    else { setBalanceLoading(false); setBillsLoading(false); }
  }, [selectedProviderId, refreshVersion]);
  const refreshAll = async () => { await loadData(); setRefreshVersion(v => v + 1); };

  // ========== Provider CRUD ==========
  const openProviderModal = (record) => {
    setEditingProvider(record);
    if (record) {
      providerForm.setFieldsValue({
        name: record.name, display_name: record.display_name,
        api_base: record.api_base, api_key: record.api_key,
        description: record.description || 'Seedance 视频生成提供商',
      });
    } else {
      providerForm.resetFields();
      providerForm.setFieldsValue({
        name: 'seedance-upstream', display_name: 'Seedance (火山引擎)',
        api_base: 'https://gzuser.zjlinghu.com',
        description: 'Seedance 视频生成提供商',
      });
    }
    setProviderModal(true);
  };

  const saveProvider = async () => {
    try {
      const values = await providerForm.validateFields();
      setSaving(true);
      if (editingProvider) {
        await providerAPI.updateProvider(editingProvider.id, values);
        message.success('提供商已更新');
      } else {
        await providerAPI.createProvider(values);
        message.success('提供商已创建');
      }
      setProviderModal(false);
      loadData();
    } catch (e) { if (e.errorFields) return; message.error('保存失败: ' + (e.response?.data?.message || e.message || '未知错误')); }
    finally { setSaving(false); }
  };

  const deleteProvider = async (record) => {
    try { await providerAPI.deleteProvider(record.id); message.success('提供商已删除'); loadData(); }
    catch (e) { message.error('删除失败: ' + (e.response?.data?.message || e.message || '未知错误')); }
  };

  // ========== Model Config CRUD ==========
  const openModelModal = (record) => {
    setEditingModel(record);
    if (record) {
      let vc = {};
      try { vc = JSON.parse(record.video_config || '{}'); } catch (e) {}
      modelForm.setFieldsValue({
        model_name: record.model_name, display_name: record.display_name, provider_id: record.provider_id,
        type: record.type || 'video', api_path: record.api_path || '',
        is_active: record.is_active !== false,
        supported_modes: vc.supported_modes || ['t2v'],
        billing_mode: vc.billing_mode || 'token_based',
        video_config_json: JSON.stringify(vc, null, 2),
      });
    } else {
      modelForm.resetFields();
      modelForm.setFieldsValue({ type: 'video', is_active: true,
        billing_mode: 'token_based', supported_modes: ['t2v', 'i2v', 'firstlast'] });
    }
    setModelModal(true);
  };

  const saveModel = async () => {
    try {
      const values = await modelForm.validateFields();
      setSaving(true);
      let vc = {};
      if (values.video_config_json && values.video_config_json.trim()) {
        try { vc = JSON.parse(values.video_config_json); } catch (e) { message.error('video_config JSON 格式错误: ' + e.message); setSaving(false); return; }
      } else {
        vc = { supported_modes: values.supported_modes || [], billing_mode: values.billing_mode || 'token_based' };
      }
      const billingType = values.billing_mode === 'token_based' ? 'per_token' : values.billing_mode === 'output_only' ? 'per_second' : (values.billing_mode || 'per_token');
      const data = { model_name: values.model_name, display_name: values.display_name || values.model_name, provider_id: values.provider_id,
        type: values.type || 'video', billing_type: billingType,
        input_price: values['720p_price'] || 0, output_price: values['720p_price'] || 0,
        api_path: values.api_path || '',
        video_config: JSON.stringify(vc), is_active: values.is_active !== false };
      if (editingModel) {
        await providerAPI.updateModelConfig(editingModel.id, data);
        message.success('模型配置已更新');
      } else {
        await providerAPI.createModelConfig(data);
        message.success('模型配置已创建');
      }
      setModelModal(false); loadData();
    } catch (e) { if (e.errorFields) return; message.error('保存失败: ' + (e.response?.data?.message || e.message || '未知错误')); }
    finally { setSaving(false); }
  };

  const deleteModel = async (record) => {
    try { await providerAPI.deleteModelConfig(record.id); message.success('模型配置已删除'); loadData(); }
    catch (e) { message.error('删除失败: ' + (e.response?.data?.message || e.message || '未知错误')); }
  };

  // ========== Columns ==========
  const providerColumns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    { title: '名称', dataIndex: 'name', width: 180 },
    { title: '显示名', dataIndex: 'display_name', width: 180 },
    { title: 'API 地址', dataIndex: 'api_base', ellipsis: true, render: v => <Text copyable style={{ fontSize: 12 }}>{v}</Text> },
    { title: '状态', dataIndex: 'is_active', width: 80, render: v => v !== false ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag> },
    { title: '操作', width: 150, render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => openProviderModal(r)}>编辑</Button>
        <Popconfirm title="确定删除？" onConfirm={() => deleteProvider(r)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )},
  ];

  const modelColumns = [
    { title: 'ID', dataIndex: 'id', width: 45 },
    { title: '模型名', dataIndex: 'model_name', width: 160, render: v => <Text code>{v}</Text> },
    { title: '提供商', key: 'prov', width: 70, render: (_, r) => { const p = providers.find(x => x.id === r.provider_id); return <Tag>{p?.display_name || p?.name || r.provider_id}</Tag>; } },
    { title: '定价(分辨率/无参考/有参考)', key: 'price', width: 180, render: (_, r) => { let vc = {}; try { vc = JSON.parse(r.video_config || '{}'); } catch (e) {}; const rp = vc.resolution_pricing || {}; const rpw = vc.resolution_pricing_with_video || {}; const keys = Object.keys(rp); if (!keys.length) return <Text type="secondary">-</Text>; return <div style={{fontSize:11,lineHeight:'1.8'}}>{keys.map(k => <div key={k}>{k.toUpperCase()}<span style={{margin:'0 4px',color:'#ddd'}}>/</span>¥{rp[k]}{rpw[k]!=null?<><span style={{margin:'0 4px',color:'#ddd'}}>/</span><span style={{color:'#52c41a'}}>¥{rpw[k]}</span></>:null}</div>)}</div>; } },
    { title: '模式', key: 'modes', width: 180, render: (_, r) => { let vc = {}; try { vc = JSON.parse(r.video_config || '{}'); } catch (e) {}; const modes = vc.supported_modes || []; return modes.length > 0 ? modes.map(m => { const found = VIDEO_MODES.find(v => v.value === m); return <Tag key={m} color="blue" style={{ fontSize: 11 }}>{found?.label || m}</Tag>; }) : <Text type="secondary">-</Text>; } },
    { title: '计费', dataIndex: 'billing_type', width: 80, render: v => <Tag>{v || '-'}</Tag> },
    { title: '状态', dataIndex: 'is_active', width: 55, render: v => v !== false ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag> },
    { title: '操作', width: 120, render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => openModelModal(r)} />
        <Popconfirm title="确定删除？" onConfirm={() => deleteModel(r)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )},
  ];

  const hasFormula = (bills.list || []).some(b => b.formula);
  const hasTokens = (bills.list || []).some(b => b.output_tokens > 0 || b.input_tokens > 0);
  const billColumns = [
    { title: '时间', dataIndex: 'created_at', width: 110, render: v => v?.replace('T', ' ').slice(0, 16) },
    { title: '模型', dataIndex: 'model', width: 120 },
    { title: '金额', dataIndex: 'amount', width: 80, render: v => <Text style={{ color: '#cf1322' }}>¥{v?.toFixed(4)}</Text> },
    ...(hasTokens ? [{ title: 'Token用量', key: 'tokens', width: 90, render: (_, r) => <Text>{((r.output_tokens||0)+(r.input_tokens||0)).toLocaleString()}</Text> }] : []),
    { title: '余额前', dataIndex: 'balance_before', width: 75, render: v => `¥${v?.toFixed(2)}` },
    { title: '余额后', dataIndex: 'balance_after', width: 75, render: v => `¥${v?.toFixed(2)}` },
    ...(hasFormula ? [{ title: '公式', dataIndex: 'formula', width: 340 }] : []),
  ];

  const dailyData = upstreamUsage?.daily_summary || [];
  const todayData = todaySeedanceSummary(dailyData);
  const totalCost = upstreamUsage?.total_consumption || upstreamUsage?.model_summary?.reduce((s, m) => s + (m.total_cost || 0), 0) || 0;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <ThunderboltOutlined style={{ color: '#1677ff', marginRight: 8 }} />
            Seedance 视频生成配置
          </Title>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refreshAll} loading={loading || balanceLoading || billsLoading}>刷新全部</Button>
          {activeTab === 'providers' ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openProviderModal(null)}>添加提供商</Button>
          ) : activeTab === 'models' ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openModelModal(null)}>添加模型配置</Button>
          ) : (
            <Button icon={<ReloadOutlined />} onClick={() => loadBills(billsPage)} loading={billsLoading}>刷新账单</Button>
          )}
        </Space>
      </div>

      {/* 第一行：账户概览 + 计费算法 — 严格等高 */}
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={24} lg={9}>
          <Card size="small" title={<span><WalletOutlined /> 账户概览</span>} extra={<Select aria-label="查询账户" placeholder="选择供应商账户" value={selectedProviderId} onChange={setSelectedProviderId} style={{ width: 200 }} options={accountProviders.map(p => ({ value: p.id, label: p.display_name || p.name }))} />} styles={{ body: { padding: '10px 14px', minHeight: 142, display: 'flex', alignItems: 'center' } }}>
            <Row gutter={8} align="middle" style={{ width: '100%' }}>
              <Col span={8} style={{ textAlign: 'center', borderRight: '1px solid #f0f0f0' }}>
                <div style={{ fontSize: 12, color: '#888' }}>上游余额</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: (upstreamBalance?.balance || 0) > 10 ? '#3f8600' : '#cf1322', lineHeight: 1.2 }}>
                  ¥ {accountNumber(upstreamBalance?.balance)?.toFixed(2) ?? '--'}
                </div>
                <Button type="link" size="small" icon={balanceLoading ? <LoadingOutlined /> : <ReloadOutlined />} onClick={() => loadStats()} disabled={balanceLoading || !selectedProviderId} style={{ padding: 0, height: 20 }} />
              </Col>
              <Col span={8} style={{ textAlign: 'center', borderRight: '1px solid #f0f0f0' }}>
                <div style={{ fontSize: 12, color: '#888' }}>今日消费</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#cf1322', lineHeight: 1.2 }}>
                  ¥ {upstreamUsage ? (accountNumber(todayData.total_cost)?.toFixed(2) ?? '--') : '--'}
                </div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  累计 ¥ {upstreamUsage ? (accountNumber(totalCost)?.toFixed(2) ?? '--') : '--'}
                </Text>
              </Col>
              <Col span={8} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#888' }}>今日调用</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#1677ff', lineHeight: 1.2 }}>
                  {upstreamUsage ? (accountNumber(todayData.total_calls) ?? '--') : '--'}
                </div>
                <Text type="secondary" style={{ fontSize: 11 }}>次</Text>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} lg={15}>
          <Card size="small" title={<span><CalculatorOutlined /> 计费算法</span>} styles={{ body: { padding: '8px 14px', minHeight: 142 } }}>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong style={{ fontSize: 13 }}>Token 计算</Text>
                <div style={{ fontSize: 12, color: '#555', lineHeight: 1.7, marginTop: 4 }}>
                  <div>tokens = (input+output) × W × H / 1024</div>
                  <div style={{ color: '#999', fontSize: 11 }}>output = dur×fps+1, input = refVideo时 dur×fps+1</div>
                  <div>参考视频 input ≥ 4s 保底，每段 +1 I帧</div>
                  <div style={{ color: '#1677ff', fontSize: 11, marginTop: 2 }}>例: 5s 720p → (0+121)×1280×720/1024=108,900 tokens</div>
                </div>
              </Col>
              <Col span={12}>
                <Text strong style={{ fontSize: 13 }}>计费方式</Text>
                <div style={{ fontSize: 12, color: '#555', lineHeight: 1.7, marginTop: 4 }}>
                  <div>费用 = tokens × 单价 / 1,000,000</div>
                  <div style={{ color: '#999', fontSize: 11 }}>有 refVideo → with_video 折扣价</div>
                  <div>提交预扣估算 → 完成按 completion_tokens 实扣修正</div>
                  <div style={{ color: '#999', fontSize: 11 }}>多退少补，失败自动回滚</div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {(statsError || billsError) && <Alert type="error" showIcon message="上游账户数据未全部刷新成功" description={[statsError, billsError && `账单：${billsError}`].filter(Boolean).join('；')} style={{ marginBottom: 12 }} />}
      {!selectedProviderId && <Alert type="info" showIcon message="请先配置并启用 Seedance 供应商，再查询账户数据" style={{ marginBottom: 12 }} />}
      {/* 接入指南 - 紧凑 */}
      <Alert
        message="接入流程：① 添加 Seedance 上游提供商（API 地址 + Key） → ② 在「模型配置」添加具体模型（定价、生成模式） → ③ 用户端视频生成页面下拉选择 Seedance 模型使用"
        type="info" showIcon={false}
        style={{ marginBottom: 12, padding: '6px 14px' }}
      />

      {/* 标签页 */}
      <Card styles={{ body: { paddingTop: 4 } }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: 'providers',
            label: <span><ApiOutlined /> 提供商管理</span>,
            children: (
              <Table dataSource={seedanceProviders} columns={providerColumns} rowKey="id" loading={loading} size="middle" pagination={false}
                locale={{ emptyText: '暂无提供商，请先添加 Seedance 提供商' }} />
            ),
          },
          {
            key: 'models',
            label: <span><ThunderboltOutlined /> 模型配置</span>,
            children: (
              <Table dataSource={seedanceModels} columns={modelColumns} rowKey="id" loading={loading} size="middle" pagination={false}
                locale={{ emptyText: '暂无模型配置，请先添加模型' }} />
            ),
          },
          {
            key: 'bills',
            label: <span><FileTextOutlined /> 上游账单</span>,
            children: (
              <Table dataSource={bills.list || []} columns={billColumns} rowKey="id" loading={billsLoading}
                  pagination={{ current: billsPage, pageSize: 15, total: bills.total || 0, onChange: (p) => loadBills(p), showTotal: t => `共 ${t} 条` }} />
            ),
          },
        ]} />
      </Card>

      {/* Provider Modal */}
      <Modal title={editingProvider ? '编辑提供商' : '添加 Seedance 提供商'} open={providerModal} onCancel={() => setProviderModal(false)} onOk={saveProvider} confirmLoading={saving} width={560} destroyOnHidden>
        <Form form={providerForm} layout="vertical">
          <Form.Item name="name" label="提供商标识" rules={[{ required: true }]}><Input placeholder="如 seedance-upstream" /></Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true }]}><Input placeholder="如 Seedance (火山引擎)" /></Form.Item>
          <Form.Item name="api_base" label="API 基础地址" rules={[{ required: true }]}><Input placeholder="https://上游域名" /></Form.Item>
          <Form.Item name="api_key" label="API Key" rules={[{ required: true }]}><Input.Password placeholder="上游 API Key" /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* Model Modal */}
      <Modal title={editingModel ? '编辑模型配置' : '添加 Seedance 模型'} open={modelModal} onCancel={() => setModelModal(false)} onOk={saveModel} confirmLoading={saving} width={640} destroyOnHidden>
        <Form form={modelForm} layout="vertical">
          <Form.Item name="model_name" label="模型名称" extra="同一供应商内唯一；不同供应商可配置相同模型，调用时使用供应商名/模型名。" rules={[{ required: true }]}><Input placeholder="如 seedance-2.0-idle" /></Form.Item>
          <Form.Item name="display_name" label="显示名称"><Input placeholder="如 Seedance 2.0 Idle" /></Form.Item>
          <Form.Item name="provider_id" label="提供商 ID" rules={[{ required: true }]}>
            <Select placeholder="选择提供商" options={providers.map(p => ({ value: p.id, label: `${p.display_name || p.name} (ID:${p.id})` }))} />
          </Form.Item>
          <Form.Item name="api_path" label="API 路径"><Input placeholder="/v1/video/generations" /></Form.Item>
          <Form.Item name="type" label="模型类型"><Select options={[{ value: 'video', label: '视频生成 (video)' }]} /></Form.Item>
          <Form.Item name="is_active" label="启用"><Select options={[{ value: true, label: '启用' }, { value: false, label: '禁用' }]} /></Form.Item>
          <Divider>支持的生成模式</Divider>
          <Form.Item name="supported_modes"><Select mode="multiple" placeholder="选择支持的生成模式" options={VIDEO_MODES} /></Form.Item>
          <Divider>完整 JSON 配置</Divider>
	          <Form.Item name="video_config_json" label="video_config JSON" extra="直接编辑 JSON 配置，包含分辨率、时长、比例、参考图上限等所有参数。留空则使用下方分字段配置。" rules={[{ validator: (_, v) => { if (!v) return Promise.resolve(); try { JSON.parse(v); return Promise.resolve(); } catch(e) { return Promise.reject('JSON 格式错误'); } } }]}>
	            <Input.TextArea rows={10} style={{ fontFamily: 'monospace', fontSize: 12 }} placeholder='{"resolutions":[...],"durationRange":[4,15],...}' />
	          </Form.Item>
	          <Divider>分字段配置（JSON 留空时使用）</Divider>
	          <Form.Item name="billing_mode" label="计费模式">
            <Select options={[
              { value: 'token_based', label: 'Token 公式计费 (Seedance)' },
              { value: 'output_only', label: '仅输出计时 (HappyHorse)' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SeedanceConfig;
